import { ReactNode, useEffect, useState } from 'react'
import CartContext, { ICartProduct, IOrderProduct } from './contexts/CartContext'
import { getProduct } from './features/products'
import { IProduct } from './types/IProduct'

const CART_PRODUCTS_LOCAL_STORAGE_KEY = 'cartProducts-v2'
const CART_PRODUCTS_CHANGED = 'storage'

const dispatchCartProductsChanged = () => {
  window.dispatchEvent(new Event(CART_PRODUCTS_CHANGED))
}

const getSearchParamsCartProducts = (): ICartProduct[] | null => {
  const searchParams = new URLSearchParams(window.location.search)
  const searchParamsOrderProducts = searchParams.get('cart')

  return searchParamsOrderProducts ? JSON.parse(searchParamsOrderProducts) : null
}

function getCartProductsFromLocalStorage(): IOrderProduct[] {
  const localStorageCartProducts = window.localStorage.getItem(CART_PRODUCTS_LOCAL_STORAGE_KEY)

  return localStorageCartProducts ? JSON.parse(localStorageCartProducts) : []
}

function normalizeCartProductsFromLocalStorage(products: IProduct[]): ICartProduct[] {
  return getCartProductsFromLocalStorage().map((cartProductData) => {
    const product = getProduct(products, cartProductData.id)

    if (!product) throw new Error(`Can't find product by id ${cartProductData.id}`)

    return {
      data: cartProductData,
      product,
    }
  })
}

const getCartProducts = (
  products: IProduct[]
): {
  products: ICartProduct[]
  isOrder: boolean
} => {
  const orderCartProducts = getSearchParamsCartProducts()

  if (orderCartProducts) {
    return {
      products: orderCartProducts,
      isOrder: true,
    }
  }

  return {
    products: normalizeCartProductsFromLocalStorage(products),
    isOrder: false,
  }
}

export function Providers({ children }: { children: ReactNode }) {
  const [cartProductsState, setCartProducts] = useState<ICartProduct[]>([])
  const [isVisibleCart, setIsVisibleCart] = useState(false)
  const [products, setProducts] = useState<IProduct[] | null>(null)

  useEffect(() => {
    const init = async () => {
      const res = await fetch(`${process.env.REACT_APP_API_HOST}/api/products`)

      const json = await res.json()

      setProducts(json.data)
    }

    init()
  }, [])
  // const router = useRouter()
  //
  // const handleCartRouterReplace = useCallback((orderCartProducts: ICartProduct[]) => {
  //   const params = new URLSearchParams()
  //
  //   params.append('cart', JSON.stringify(orderCartProducts))
  //
  //   router.replace(`?${params.toString()}`, {
  //     scroll: false
  //   })
  // }, [])

  const handleSetProducts = (cartProducts: ICartProduct[]) => {
    window.localStorage.setItem(
      CART_PRODUCTS_LOCAL_STORAGE_KEY,
      JSON.stringify(cartProducts.map((cartProduct) => cartProduct.data))
    )
    dispatchCartProductsChanged()
    setCartProducts(cartProducts)
  }

  const addToCart = (id: string, quantity = 1) => {
    if (!products) {
      throw new Error('No products')
    }

    const cartProducts = getCartProducts(products)

    if (!cartProducts.products.some((cartProduct) => cartProduct.data.id === id)) {
      const product = getProduct(products, id)

      if (!product) throw new Error(`[addToCart ERROR]: Can't find product by id ${id}`)

      const _products: ICartProduct[] = [
        ...cartProducts.products,
        {
          data: {
            id,
            quantity,
          },
          product,
        },
      ]

      if (cartProducts.isOrder) {
        // handleCartRouterReplace(cartProducts.products)
      } else {
        handleSetProducts(_products)
      }
    }
  }

  const removeFromCart = (id: string) => {
    if (!products) {
      throw new Error('No products')
    }

    const cartProducts = getCartProducts(products)
    const _products = cartProducts.products.filter((cartProduct) => cartProduct.data.id !== id)

    if (cartProducts.isOrder) {
      // handleCartRouterReplace(products)
    } else {
      handleSetProducts(_products)
    }
  }

  const updateCartProduct = (id: string, quantity = 1) => {
    if (!products) {
      throw new Error('No products')
    }

    const cartProducts = getCartProducts(products)
    const _products = [...cartProducts.products]
    const productIndex = _products.findIndex((cartProduct) => cartProduct.data.id === id)

    if (productIndex > -1) {
      const product: ICartProduct = { ..._products[productIndex], data: { id, quantity } }

      _products[productIndex] = product

      if (cartProducts.isOrder) {
        // handleCartRouterReplace(products)
      } else {
        handleSetProducts(_products)
      }
    }
  }

  const cartReset = () => {
    handleSetProducts([])
  }

  useEffect(() => {
    if (!products) {
      return
    }

    const cartProductsDataItems = getCartProductsFromLocalStorage()
    const handleStorage = () => {
      setCartProducts(getCartProducts(products).products)
    }

    try {
      setCartProducts(normalizeCartProductsFromLocalStorage(products))
      window.addEventListener(CART_PRODUCTS_CHANGED, handleStorage)
    } catch (error) {
      const formData = new FormData()

      if (error instanceof Error) {
        formData.append('errorMessage', `[CART_ERROR]: ${error.message}`)
      } else {
        formData.append('errorMessage', `[CART_ERROR]: ${error as string}`)
      }

      formData.append('data', JSON.stringify(cartProductsDataItems) || '')
      handleSetProducts([])

      fetch('/api/errors', {
        method: 'POST',
        body: formData,
      })
    }

    return () => {
      window.removeEventListener(CART_PRODUCTS_CHANGED, handleStorage)
    }
  }, [products])

  return (
    <CartContext.Provider
      value={{
        products,
        cartProducts: cartProductsState,
        addToCart,
        removeFromCart,
        updateCartProduct,
        isVisibleCart,
        setIsVisibleCart,
        reset: cartReset,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}
